import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Dog, Cat, AlertTriangle, MapPin, Search, Plus, Users, BadgeCheck, X, Check, ChevronsUpDown, PiggyBank, SlidersHorizontal, PawPrint, Footprints, HeartHandshake } from "lucide-react";
import { HouseHeart } from "@/components/icons/HouseHeart";
import { Link } from "react-router-dom";
import type { ShelterFilters, Shelter } from "@/types/shelter";
import { trackEvent } from "@/hooks/useEventTracking";
import { shelterFields } from "@/lib/analyticsEvents";
import { isUrgentNeedsActive } from "@/types/shelter";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { includesNormalized, matchesWordPrefix } from "@/lib/geoText";

interface ShelterFiltersProps {
  filters: ShelterFilters;
  onFiltersChange: (filters: ShelterFilters) => void;
  cities: string[];
  resultsCount: number;
  totalCount: number;
  shelters?: Shelter[];
  shelterIdsWithFundraiser?: Set<string>;
  shelterIdsWithNamedAdoptions?: Set<string>;
  shelterIdsWithUrgentSection?: Set<string>;
}

// Klucze filtrow ukrytych w popoverze "Wiecej filtrow".
// Lista jest jednoczesnie zrodlem prawdy dla checkboxow i licznika aktywnych.
type HiddenFilterKey = "volunteering" | "dogWalking" | "dogsAvailable" | "catsAvailable";

const HIDDEN_FILTERS: Array<{
  key: HiddenFilterKey;
  label: string;
  icon: typeof Users;
}> = [
  { key: "volunteering", label: "Wolontariat", icon: HeartHandshake },
  { key: "dogWalking", label: "Spacery z psami", icon: Footprints },
  { key: "dogsAvailable", label: "Możliwość adopcji psów", icon: Dog },
  { key: "catsAvailable", label: "Możliwość adopcji kotów", icon: Cat },
];


export const ShelterFiltersComponent = ({ 
  filters, 
  onFiltersChange, 
  cities,
  resultsCount,
  totalCount,
  shelters = [],
  shelterIdsWithFundraiser,
  shelterIdsWithNamedAdoptions,
  shelterIdsWithUrgentSection,
}: ShelterFiltersProps) => {
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Local state for the input value (immediate) + debounced actual filter
  const [localSearchQuery, setLocalSearchQuery] = useState(filters.searchQuery);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Sync local state if external filters.searchQuery changes (e.g. suggestion click)
  useEffect(() => {
    setLocalSearchQuery(filters.searchQuery);
  }, [filters.searchQuery]);

  const debouncedUpdateFilter = useCallback((value: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      onFiltersChange({ ...filters, searchQuery: value });
    }, 300);
  }, [filters, onFiltersChange]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const toggleFilter = (key: keyof Omit<ShelterFilters, 'city' | 'searchQuery'>) => {
    const newValue = !filters[key];
    trackEvent("Filtr", { filter: key, enabled: newValue });
    onFiltersChange({ ...filters, [key]: newValue });
  };

  // Filter cities based on shelters that match current filters (except city filter itself)
  const filteredCities = useMemo(() => {
    const sheltersMatchingFilters = shelters.filter(shelter => {
      if (filters.urgentNeeds && !isUrgentNeedsActive(shelter) && !shelterIdsWithUrgentSection?.has(shelter.id)) return false;
      if (filters.hasFundraiser && !shelterIdsWithFundraiser?.has(shelter.id)) return false;
      if (filters.hasNamedAdoptions && !shelterIdsWithNamedAdoptions?.has(shelter.id)) return false;
      if (filters.volunteering && !shelter.volunteering_available) return false;
      if (filters.dogWalking && !shelter.dog_walking_available) return false;
      if (filters.dogsAvailable && !shelter.dogs_available) return false;
      if (filters.catsAvailable && !shelter.cats_available) return false;
      if (filters.organizationVerified && !shelter.organization_verified) return false;
      if (filters.searchQuery) {
        if (!matchesWordPrefix(shelter.name, filters.searchQuery) && 
            !includesNormalized(shelter.city, filters.searchQuery) &&
            !(shelter.region && includesNormalized(shelter.region, filters.searchQuery))) {
          return false;
        }
      }
      return true;
    });
    
    const uniqueCities = [...new Set(sheltersMatchingFilters.map(s => s.city))].sort();
    return uniqueCities;
  }, [shelters, filters.urgentNeeds, filters.hasFundraiser, filters.hasNamedAdoptions, filters.volunteering, filters.dogWalking, filters.dogsAvailable, filters.catsAvailable, filters.organizationVerified, filters.searchQuery, shelterIdsWithFundraiser, shelterIdsWithNamedAdoptions, shelterIdsWithUrgentSection]);

  const hiddenActiveCount = useMemo(
    () => HIDDEN_FILTERS.reduce((acc, f) => acc + (filters[f.key] ? 1 : 0), 0),
    [filters],
  );

  const handleClearHiddenFilters = () => {
    const cleared = { ...filters };
    HIDDEN_FILTERS.forEach((f) => {
      if (cleared[f.key]) {
        trackEvent("Filtr", { filter: f.key, enabled: false });
        (cleared as Record<HiddenFilterKey, boolean>)[f.key] = false;
      }
    });
    onFiltersChange(cleared);
  };

  // Filter suggestions based on local search query (immediate for suggestions)
  // Includes shelters (by name/city) AND matching voivodeships (regions)
  const suggestions = useMemo(() => {
    if (localSearchQuery.length < 2) return [] as Array<
      | { kind: "shelter"; shelter: Shelter }
      | { kind: "region"; region: string; count: number }
    >;
    const shelterMatches = shelters
      .filter(s =>
        matchesWordPrefix(s.name, localSearchQuery) ||
        includesNormalized(s.city, localSearchQuery)
      )
      .slice(0, 10)
      .map(shelter => ({ kind: "shelter" as const, shelter }));

    const regionCounts = new Map<string, number>();
    shelters.forEach(s => {
      if (s.region && includesNormalized(s.region, localSearchQuery)) {
        regionCounts.set(s.region, (regionCounts.get(s.region) || 0) + 1);
      }
    });
    const regionMatches = [...regionCounts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([region, count]) => ({ kind: "region" as const, region, count }));

    return [...regionMatches, ...shelterMatches];
  }, [shelters, localSearchQuery]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      const selected = suggestions[selectedIndex];
      if (selected.kind === "region") {
        handleSelectRegion(selected.region);
      } else {
        handleSelectSuggestion(selected.shelter);
      }
      setShowSuggestions(false);
      setSelectedIndex(-1);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectSuggestion = (shelter: Shelter) => {
    trackEvent("Wyszukiwanie - wybrano", shelterFields(shelter));
    onFiltersChange({ ...filters, searchQuery: shelter.name });
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  const handleSelectRegion = (region: string) => {
    trackEvent("Wyszukiwanie - wybrano województwo", { region });
    onFiltersChange({ ...filters, searchQuery: region });
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  return (
    <div className="filters-surface px-3 py-2.5 sm:px-6 sm:py-4">
      {/* Row 1: Search + Add button (Add hidden on mobile) */}
      <div className="flex gap-2 sm:gap-3 mb-2 sm:mb-2.5">
        {/* Search input - glassy, subtle ring */}
        <div className="relative flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary/70 z-10" />
          <Input
            ref={inputRef}
            type="text"
            placeholder="Szukaj schroniska..."
            value={localSearchQuery}
            onChange={(e) => {
              setLocalSearchQuery(e.target.value);
              debouncedUpdateFilter(e.target.value);
              setShowSuggestions(true);
              setSelectedIndex(-1);
            }}
            onFocus={() => setShowSuggestions(true)}
            onKeyDown={handleKeyDown}
            className="pl-8 sm:pl-10 pr-9 sm:pr-3 bg-card h-9 sm:h-9 text-base sm:text-xs text-foreground placeholder:text-foreground/60 rounded-full border-transparent shadow-sm hover:border-primary/30 hover:shadow focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:border-primary transition-all"
            autoComplete="off"
          />
          {/* Clear button - mobile only */}
          {localSearchQuery && (
            <button
              type="button"
              onClick={() => {
                setLocalSearchQuery("");
                onFiltersChange({ ...filters, searchQuery: "" });
                setShowSuggestions(false);
                inputRef.current?.focus();
              }}
              className="sm:hidden absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-muted/80 active:bg-muted/60 z-10"
              aria-label="Wyczyść wyszukiwanie"
            >
              <X className="w-3 h-3" />
            </button>
          )}
          
          {/* Autocomplete suggestions */}
          {showSuggestions && suggestions.length > 0 && (
            <div 
              ref={suggestionsRef}
              className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl sm:rounded-lg shadow-lg z-[2000] overflow-y-auto max-h-[60vh] overscroll-contain"
            >
              {suggestions.map((item, index) => {
                if (item.kind === "region") {
                  return (
                    <button
                      key={`region-${item.region}`}
                      onClick={() => handleSelectRegion(item.region)}
                      className={`w-full px-3 py-2 text-left hover:bg-accent transition-colors flex items-center justify-between ${
                        index === selectedIndex ? 'bg-accent' : ''
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <MapPin className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium text-card-foreground text-sm truncate">{item.region}</p>
                          <p className="text-[11px] text-muted-foreground truncate">Województwo · {item.count} {item.count === 1 ? 'schronisko' : (item.count % 10 >= 2 && item.count % 10 <= 4 && (item.count % 100 < 12 || item.count % 100 > 14)) ? 'schroniska' : 'schronisk'}</p>
                        </div>
                      </div>
                    </button>
                  );
                }
                const shelter = item.shelter;
                return (
                  <button
                    key={shelter.id}
                    onClick={() => handleSelectSuggestion(shelter)}
                    className={`w-full px-3 py-2 text-left hover:bg-accent transition-colors flex items-center justify-between gap-2 ${
                      index === selectedIndex ? 'bg-accent' : ''
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-card-foreground text-sm break-words">{shelter.name}</p>
                      <p className="text-xs text-muted-foreground break-words">{shelter.city}</p>

                    </div>
                    {isUrgentNeedsActive(shelter) && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-urgent/10 text-urgent rounded-full font-medium shrink-0">
                        Pilne
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <Button asChild variant="outline" size="icon" className="sm:hidden shrink-0 h-9 w-9 rounded-full bg-card border-transparent text-foreground hover:border-primary/30 hover:bg-primary/5 hover:text-primary shadow-sm hover:shadow transition-all" aria-label="Dodaj schronisko">
          <Link to="/dodaj-schronisko">
            <Plus className="w-4 h-4" />
            <span className="sr-only">Dodaj schronisko</span>
          </Link>
        </Button>

        <Button asChild variant="outline" size="sm" className="hidden sm:inline-flex shrink-0 h-9 gap-1.5 rounded-full bg-card border-transparent text-foreground text-xs font-medium hover:border-primary/30 hover:bg-primary/5 hover:text-primary shadow-sm hover:shadow px-4 transition-all">
          <Link to="/dodaj-schronisko">
            <Plus className="w-4 h-4" />
            <span>Dodaj schronisko</span>
          </Link>
        </Button>
      </div>

      {/* Row 2: City dropdown + Filter Chips — all in one line */}
      <div className="flex flex-wrap gap-1.5 sm:gap-3 items-center">
        {/* City as a filter chip style */}
        <Popover>
          <PopoverTrigger asChild>
            <button className={`filter-chip inline-flex items-center gap-1.5 ${
              filters.city ? 'filter-chip-active' : 'filter-chip-inactive'
            }`}>
              <MapPin className="w-4 h-4" />
              <span>{filters.city || "Miasto"}</span>
              <ChevronsUpDown className="w-3 h-3 opacity-50" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[280px] sm:w-[220px] p-0 z-[2100] bg-popover border border-border shadow-2xl rounded-xl overflow-hidden" align="start" side="bottom" sideOffset={4} onOpenAutoFocus={(e) => e.preventDefault()}>
            <Command className="bg-popover">
              <CommandInput placeholder="Szukaj miasta..." className="text-base sm:text-sm" />
              <CommandList className="max-h-[200px] overflow-y-auto">
                <CommandEmpty className="text-xs sm:text-sm py-4 text-center">Nie znaleziono</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    value="all"
                    onSelect={() => { trackEvent("Filtr miasto", { city: "all" }); onFiltersChange({ ...filters, city: "" }); }}
                    className="text-xs sm:text-sm py-1.5"
                  >
                    <Check className={`w-3.5 h-3.5 mr-2 ${!filters.city ? 'opacity-100' : 'opacity-0'}`} />
                    Wszystkie miasta
                  </CommandItem>
                  {filteredCities.map((city) => (
                    <CommandItem
                      key={city}
                      value={city}
                      onSelect={() => { trackEvent("Filtr miasto", { city }); onFiltersChange({ ...filters, city: city }); }}
                      className="text-xs sm:text-sm py-1.5"
                    >
                      <Check className={`w-3.5 h-3.5 mr-2 ${filters.city === city ? 'opacity-100' : 'opacity-0'}`} />
                      {city}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <button
          onClick={() => toggleFilter('organizationVerified')}
          className={`filter-chip inline-flex items-center gap-1.5 ${
            filters.organizationVerified ? 'filter-chip-active' : 'filter-chip-inactive'
          }`}
        >
          <BadgeCheck className="w-4 h-4" />
          <span>Zweryfikowane</span>
        </button>

        <button
          onClick={() => toggleFilter('urgentNeeds')}
          className={`filter-chip inline-flex items-center gap-1.5 ${
            filters.urgentNeeds ? 'filter-chip-active' : 'filter-chip-inactive'
          }`}
        >
          <AlertTriangle className="w-4 h-4" />
          <span>Pilne sprawy</span>
        </button>

        <button
          onClick={() => toggleFilter('hasFundraiser')}
          className={`filter-chip inline-flex items-center gap-1.5 ${
            filters.hasFundraiser ? 'filter-chip-active' : 'filter-chip-inactive'
          }`}
        >
          <PiggyBank className="w-4 h-4" />
          <span>Zbiórki</span>
        </button>

        <button
          onClick={() => toggleFilter('hasNamedAdoptions')}
          className={`filter-chip inline-flex items-center gap-1.5 ${
            filters.hasNamedAdoptions ? 'filter-chip-active' : 'filter-chip-inactive'
          }`}
        >
          <HouseHeart className="w-4 h-4" />
          <span>Adopcje</span>
        </button>

        <Popover open={moreFiltersOpen} onOpenChange={setMoreFiltersOpen}>
          <PopoverTrigger asChild>
            <button
              className={`filter-chip inline-flex items-center gap-1.5 ${
                hiddenActiveCount > 0 ? 'filter-chip-active' : 'filter-chip-inactive'
              }`}
              aria-label="Więcej filtrów"
            >
              <SlidersHorizontal className="w-4 h-4" />
              <span>Więcej filtrów{hiddenActiveCount > 0 ? ` · ${hiddenActiveCount}` : ''}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[240px] p-0 z-[2100] bg-popover/95 backdrop-blur-xl border border-border/60 shadow-[0_20px_50px_-12px_hsl(var(--primary)/0.25)] rounded-xl overflow-hidden origin-top transition-all data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95 data-[state=open]:slide-in-from-top-2 data-[state=closed]:slide-out-to-top-2 data-[state=open]:duration-200 data-[state=closed]:duration-150 ease-out"
            align="start"
            side="bottom"
            sideOffset={6}
          >
            <div className="p-1">
              {HIDDEN_FILTERS.map(({ key, label, icon: Icon }) => {
                const active = !!filters[key];
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleFilter(key)}
                    className={`group w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-left transition-all duration-150 ${
                      active ? 'bg-primary/8 hover:bg-primary/12' : 'hover:bg-accent/60'
                    }`}
                  >
                    <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-all duration-150 ${
                      active
                        ? 'bg-primary border-primary text-primary-foreground shadow-sm shadow-primary/30'
                        : 'border-border/70 bg-background group-hover:border-primary/40'
                    }`}>
                      {active && <Check className="w-2.5 h-2.5" strokeWidth={3} />}
                    </span>
                    <Icon className={`w-3.5 h-3.5 shrink-0 transition-colors ${active ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'}`} />
                    <span className={`transition-colors ${active ? 'text-foreground font-medium' : 'text-foreground/90'}`}>{label}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-between gap-2 border-t border-border/60 px-1.5 py-1.5 bg-muted/40">
              <button
                type="button"
                onClick={handleClearHiddenFilters}
                disabled={hiddenActiveCount === 0}
                className="text-[11px] font-medium px-2 py-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-40 disabled:hover:bg-transparent transition-all"
              >
                Wyczyść
              </button>
              <button
                type="button"
                onClick={() => setMoreFiltersOpen(false)}
                className="text-[11px] font-medium px-3 py-1 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm shadow-primary/20 hover:shadow-primary/30 active:scale-95 transition-all"
              >
                Gotowe
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
